import React, { useEffect, useState } from 'react';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, Grid, Card, Button, Chip, Stack, IconButton, Dialog, DialogContent, Skeleton, Badge } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { getCMS } from '../services/cms';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import api from '../services/api';

export default function AccommodationsPage() {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const theme = useTheme();
  const [bgMap, setBgMap] = useState({});

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get('/api/hotels')
      .then(res => { if (mounted) setHotels(res.data.hotels || []); })
      .catch(() => { if (mounted) setHotels([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    getCMS().then(items => {
      if (!mounted) return;
      const landing = (items || []).find(i => i.type === 'landing');
      setBgMap(landing?.contentJSON?.backgrounds || {});
    }).catch(() => {}).finally(() => { mounted = false; });
  }, []);

  const colorFromString = (str) => {
    if (!str) return theme.palette.primary.main;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      /* eslint-disable no-bitwise */
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return `#${'00000'.substring(0, 6 - c.length)}${c}`;
  };

  const openLightbox = (images = [], idx = 0) => {
    setLightboxImages(images && images.length ? images : ['']);
    setLightboxIndex(Number.isFinite(idx) ? idx : 0);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);
  const prevImage = () => setLightboxIndex(i => (i - 1 + lightboxImages.length) % lightboxImages.length);
  const nextImage = () => setLightboxIndex(i => (i + 1) % lightboxImages.length);

  return (
    <PublicLayout>
      <Box sx={{ position: 'relative', width: '100%', minHeight: '64vh', py: { xs: 6, md: 8 }, overflow: 'hidden' }}>
        {bgMap.accommodations && (
          <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${bgMap.accommodations})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', zIndex: 0 }} />
        )}
        {bgMap.accommodations && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.12)', zIndex: 0 }} />
        )}

        <Box sx={{ maxWidth: 1400, mx: 'auto', position: 'relative', zIndex: 1, p: { xs: 2, md: 3 } }}>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: { xs: 2, md: 3 }, boxShadow: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom color="primary" align="center">Accommodations</Typography>

        {loading ? (
          <Grid container spacing={4}>
            {[0,1,2].map(i => (
              <Grid item xs={12} md={6} lg={4} key={i}>
                <Card sx={{ p: 0, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                  <Skeleton variant="rectangular" height={220} />
                  <Box sx={{ p: 2 }}>
                    <Skeleton width="60%" />
                    <Skeleton width="40%" />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : hotels.length === 0 ? (
          <Typography align="center" color="text.secondary">No accommodations have been added yet.</Typography>
        ) : (
          <Box>
            {/* Hero for first hotel */}
            {hotels[0] && (
              <Card sx={{ mb: 4, overflow: 'hidden', borderRadius: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  { (hotels[0].primaryImage || (hotels[0].images && hotels[0].images[0])) ? (
                    <Box sx={{ position: 'relative' }}>
                      <img loading="lazy" src={(hotels[0].primaryImage || (hotels[0].images && hotels[0].images[0]) || '')} alt={`${hotels[0].name} - hero`} style={{ width: '100%', height: 480, objectFit: 'cover', display: 'block', cursor: 'pointer' }} onClick={() => openLightbox([...(hotels[0].primaryImage ? [hotels[0].primaryImage] : []), ...(hotels[0].images || [])], 0)} />
                      <IconButton onClick={() => openLightbox([...(hotels[0].primaryImage ? [hotels[0].primaryImage] : []), ...(hotels[0].images || [])], 0)} sx={{ position: 'absolute', left: 8, top: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}><PhotoLibraryIcon /></IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', height: 420, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No image</Box>
                  )}
                  <Box sx={{ position: 'absolute', inset: 0, background: theme.palette.mode === 'dark' ? 'linear-gradient(180deg, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0.7) 100%)' : 'linear-gradient(180deg, rgba(255,255,255,0) 35%, rgba(0,0,0,0.45) 100%)' }} />
                  <Box sx={{ position: 'absolute', left: 24, bottom: 24, color: '#fff', textShadow: theme.palette.mode === 'dark' ? '0 2px 10px rgba(0,0,0,0.8)' : '0 2px 8px rgba(0,0,0,0.6)' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', bgcolor: (t) => alpha(colorFromString(hotels[0].name), 0.12), display: 'inline-block', px: 1.5, py: 0.25, borderRadius: 1 }}>{hotels[0].name}</Typography>
                    {hotels[0].address && <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocationOnIcon fontSize="small" />{hotels[0].address}</Typography>}
                    {hotels[0].description && <Typography variant="body2" sx={{ mt: 1, maxWidth: 680 }}>{hotels[0].description}</Typography>}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      {hotels[0].phone && <Button variant="contained" color="primary" href={`tel:${hotels[0].phone}`} startIcon={<PhoneIcon />}>Call</Button>}
                      {hotels[0].website && <Button variant="outlined" color="inherit" href={hotels[0].website} startIcon={<LanguageIcon />} target="_blank" rel="noreferrer">Website</Button>}
                      {hotels[0].phone && <Button variant="contained" color="success" href={`https://wa.me/${(hotels[0].phone || '').replace(/[^\d]/g, '')}`} startIcon={<WhatsAppIcon />}>WhatsApp</Button>}
                    </Box>
                  </Box>
                </Box>
                {/* thumbnails for hero */}
                {(hotels[0].images || []).length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, p: 2, alignItems: 'center', overflowX: 'auto', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                    {(hotels[0].images || []).map((img, i) => (
                      <Box key={i} component="img" loading="lazy" src={img} alt={`${hotels[0].name} ${i+1}`} sx={{ width: 140, height: 90, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', boxShadow: 1, transition: 'transform .18s', '&:hover': { transform: 'translateY(-4px)' } }} onClick={() => openLightbox([...(hotels[0].primaryImage ? [hotels[0].primaryImage] : []), ...(hotels[0].images || [])], hotels[0].primaryImage ? i+1 : i)} />
                    ))}
                  </Box>
                )}
              </Card>
            )}

            <Grid container spacing={4}>
              {hotels.slice(1).map((hotel) => (
                <Grid item xs={12} md={6} lg={4} key={hotel._id}>
                  <Card sx={{ p: 0, borderRadius: 2, overflow: 'hidden', boxShadow: 3, transition: 'transform .18s, box-shadow .18s', '&:hover': { transform: 'translateY(-6px)', boxShadow: 6 }, bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fff' }}>
                    <Box sx={{ position: 'relative' }}>
                      { (hotel.primaryImage || (hotel.images && hotel.images[0])) ? (
                        <Box sx={{ position: 'relative' }}>
                          <img loading="lazy" src={hotel.primaryImage || (hotel.images && hotel.images[0]) || ''} alt={`${hotel.name} - thumb`} style={{ width: '100%', height: 260, objectFit: 'cover', display: 'block', cursor: 'pointer' }} onClick={() => openLightbox([...(hotel.primaryImage ? [hotel.primaryImage] : []), ...(hotel.images || [])], 0)} />
                          <Badge badgeContent={(hotel.images || []).length} color="primary" sx={{ position: 'absolute', right: 8, top: 8 }} />
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 220, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No image</Box>
                      )}
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, display: 'inline-flex', alignItems: 'center' }}>
                            <Box sx={{ width: 10, height: 28, borderRadius: 1, mr: 1, bgcolor: colorFromString(hotel.name) }} />
                            <Box sx={{ bgcolor: (t) => alpha(colorFromString(hotel.name), 0.08), px: 1, borderRadius: 0.75 }}>{hotel.name}</Box>
                          </Typography>
                          {hotel.address && <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocationOnIcon fontSize="small" />{hotel.address}</Typography>}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {Array.from({ length: Math.max(0, Math.min(5, Math.round(hotel.rating || 0))) }).map((_,i) => <StarIcon key={i} sx={{ color: '#FFB400', fontSize: 18 }} />)}
                            </Box>
                            <Box sx={{ ml: 0.5, bgcolor: (t) => alpha(colorFromString(hotel.name), 0.14), px: 1, py: '2px', borderRadius: 1, fontSize: 12, fontWeight: 600 }}>{(hotel.rating || 0).toFixed ? (Number(hotel.rating || 0).toFixed(1)) : (hotel.rating || 0)}</Box>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {hotel.phone && <IconButton href={`tel:${hotel.phone}`} aria-label="call"><PhoneIcon /></IconButton>}
                            {hotel.website && <IconButton href={hotel.website} target="_blank" rel="noreferrer" aria-label="website"><LanguageIcon /></IconButton>}
                            {hotel.phone && <IconButton href={`https://wa.me/${(hotel.phone || '').replace(/[^\d]/g, '')}`} aria-label="whatsapp"><WhatsAppIcon color="success" /></IconButton>}
                          </Stack>
                        </Box>
                      </Box>

                      {hotel.description && <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', minHeight: 42 }}>{hotel.description}</Typography>}

                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Stack direction="row" spacing={1}>
                          {(hotel.rooms || []).slice(0,2).map((r,i) => <Chip key={i} label={`${r.type} • ${r.price}`} size="small" />)}
                        </Stack>
                        <Box>
                          <Button variant="text" href={`/hotel/${hotel._id}`}>Details</Button>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Dialog open={lightboxOpen} onClose={closeLightbox} maxWidth="lg" fullWidth>
          <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
            <IconButton onClick={closeLightbox} sx={{ position: 'absolute', right: 8, top: 8, color: '#fff', zIndex: 10 }}><CloseIcon /></IconButton>
            <IconButton onClick={prevImage} sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', zIndex: 10 }}><ArrowBackIosNewIcon /></IconButton>
            <IconButton onClick={nextImage} sx={{ position: 'absolute', right: 48, top: '50%', transform: 'translateY(-50%)', color: '#fff', zIndex: 10 }}><ArrowForwardIosIcon /></IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
              {lightboxImages[lightboxIndex] ? (
                <img src={lightboxImages[lightboxIndex]} alt={`image ${lightboxIndex + 1}`} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
              ) : (
                <Box sx={{ width: '100%', height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>No image</Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
          </Box>
        </Box>
      </Box>
    </PublicLayout>
  );
}
